import { useEffect, useRef } from 'react'
import { Router, useRouter } from 'next/router'
import posthog from 'posthog-js'
import { PostHogProvider as OriginalPostHogProvider } from 'posthog-js/react'

import { PosthogSetup } from './constants'

const isPosthogEnabled = process.env.NEXT_PUBLIC_ENABLE_POSTHOG === 'true'

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter()
  const oldUrlRef = useRef('')
  const isInitialized = useRef(false)

  useEffect(() => {
    if (!isPosthogEnabled) return

    oldUrlRef.current = router.asPath
  }, [router.asPath])

  useEffect(() => {
    if (!isPosthogEnabled) return

    if (isInitialized.current || !PosthogSetup.key || !PosthogSetup.host) {
      if (process.env.NODE_ENV === 'development' && !PosthogSetup.key) {
        console.warn(
          'PostHog key is not configured. Analytics will be disabled.',
        )
      }
      if (process.env.NODE_ENV === 'development' && !PosthogSetup.host) {
        console.warn(
          'PostHog host is not configured. Analytics will be disabled.',
        )
      }
      return
    }

    isInitialized.current = true

    posthog.init(PosthogSetup.key, {
      api_host: PosthogSetup.host,
      loaded: posthog => {
        if (process.env.NODE_ENV === 'development') posthog.debug()
      },
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
      },
    })

    const handleRouteChange = () => posthog?.capture('$pageview')

    const handleRouteChangeStart = () =>
      posthog?.capture('$pageleave', { $current_url: oldUrlRef.current })

    Router.events.on('routeChangeComplete', handleRouteChange)
    Router.events.on('routeChangeStart', handleRouteChangeStart)

    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange)
      Router.events.off('routeChangeStart', handleRouteChangeStart)
    }
  }, [])

  if (!isPosthogEnabled || !PosthogSetup.key || !PosthogSetup.host) {
    return <>{children}</>
  }

  return (
    <OriginalPostHogProvider client={posthog}>
      {children}
    </OriginalPostHogProvider>
  )
}
